import type { PointRecord, Project } from '../types';

type PaidItem = { taskId: string; subTaskId?: string; record: PointRecord };
const keyFor = (taskId?: string, subTaskId?: string) => JSON.stringify([taskId ?? null, subTaskId ?? null]);

/** Only pass owner-private copies here, never relational row data. */
function paidRecords(sources: (Project | undefined)[]): Map<string, PointRecord> {
  const records = new Map<string, PointRecord>();
  const add = (key: string, record?: PointRecord) => {
    if (!record) return;
    const old = records.get(key);
    // Two owner devices converge on the first award, independent of merge direction.
    const rank = (value: PointRecord) => `${value.earnedAt}|${value.points}|${value.userLevel}`;
    if (!old || rank(record) < rank(old)) records.set(key, record);
  };
  for (const source of sources) {
    if (!source) continue;
    add(keyFor(), source.pointRecord);
    for (const task of source.tasks) {
      add(keyFor(task.id), task.pointRecord);
      for (const sub of task.subTasks ?? []) add(keyFor(task.id, sub.id), sub.pointRecord);
    }
    for (const item of source.retainedPointRecords ?? []) add(keyFor(item.taskId, item.subTaskId), item.record);
  }
  return records;
}

/** Content may be untrusted; points ALWAYS come exclusively from trusted sources. */
export function withOwnerPoints(content: Project, ...trusted: (Project | undefined)[]): Project {
  const records = paidRecords(trusted);
  const take = (taskId?: string, subTaskId?: string) => {
    const key = keyFor(taskId, subTaskId), record = records.get(key);
    records.delete(key);
    return record;
  };
  const next: Project = { ...content, pointRecord: take(), tasks: content.tasks.map(task => ({
    ...task, pointRecord: take(task.id), subTasks: task.subTasks?.map(sub => ({ ...sub, pointRecord: take(task.id, sub.id) })),
  })) };
  const retained: PaidItem[] = [...records].map(([key, record]) => {
    const [taskId, subTaskId] = JSON.parse(key) as [string, string | null];
    return { taskId, ...(subTaskId === null ? {} : { subTaskId }), record };
  }).sort((a, b) => keyFor(a.taskId, a.subTaskId).localeCompare(keyFor(b.taskId, b.subTaskId)));
  delete next.retainedPointRecords;
  if (retained.length) next.retainedPointRecords = retained;
  return next;
}

export function hasOwnerAward(project: Project, taskId?: string, subTaskId?: string): boolean {
  return paidRecords([project]).has(keyFor(taskId, subTaskId));
}

/** Existing active-item accounting is unchanged; only missing paid identities add XP. */
export function retainedProjectPoints(project: Project): number {
  const active = new Set(project.tasks.flatMap(task => [keyFor(task.id), ...(task.subTasks ?? []).map(sub => keyFor(task.id, sub.id))]));
  const records = paidRecords([{ ...project, pointRecord: undefined, tasks: [] }]);
  return [...records].reduce((sum, [key, record]) => sum + (active.has(key) ? 0 : record.points), 0);
}

export function privateTransitionWins(project: Project | undefined, revision: number): boolean {
  return !!project?.privateSince && !project.sharedMirror && project.privateSince.revision >= revision;
}
